import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axiosCustomerInstance from '../api/AxiosCustomerInstance';
import axiosRestaurantInstance from '../restaurant/AxiosRestaurantInstance';
import { CUSTOMER_API_PATHS, ORGANIZATION_API_PATHS } from '../constants/apiPaths';
import EatsAdminLayout from './EatsAdminLayout';
import EatsAdminPageHeader from './EatsAdminPageHeader';
import {
  formatAadharDisplay,
  formatAadharDisplayOrFallback,
  formatMobileDisplayOrFallback,
  sanitizeAadharInput,
  validateAadharNumber,
} from '../restaurant/formatDisplay';
import { useAutoDismissMessage } from '../utils/useAutoDismissMessage';
import { isEatsAppRootAdmin } from '../utils/eatsAppAuth';
import './EatsAdminCreatePage.css';
import './EatsAdminDashboard.css';
import './AppUsersPage.css';

function normalizeUserRow(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw.id ?? raw.userId ?? raw.user_id ?? raw.User_Id;
  if (id == null) return null;
  return {
    id: String(id),
    firstName: raw.firstName ?? raw.first_name ?? raw.First_Name ?? '',
    lastName: raw.lastName ?? raw.last_name ?? raw.Last_Name ?? '',
    name:
      raw.name ??
      raw.fullName ??
      `${raw.firstName ?? raw.first_name ?? ''} ${raw.lastName ?? raw.last_name ?? ''}`.trim(),
    email: raw.email ?? raw.Email ?? '',
    aadharNo: raw.aadharNo ?? raw.aadhar_no ?? raw.aadharNumber ?? '',
    mobile: raw.mobile ?? raw.mobileNumber ?? raw.contactNumber ?? raw.Contact_Number ?? '',
    role:
      raw.role ??
      raw.roleName ??
      (Array.isArray(raw.roles) && raw.roles.length > 0 ? raw.roles.join(', ') : ''),
    status: String(raw.status ?? raw.Status ?? 'ACTIVE').toUpperCase(),
    source:
      raw.sourceApp ??
      raw.app ??
      raw.application ??
      raw.platform ??
      raw.userType ??
      'EATS',
  };
}

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  contactNumber: '',
  aadharNumber: '',
  password: '',
  roleName: 'APP_USER',
};

const renderStatusBadge = (status) => {
  if (!status) return '-';
  const normalized = String(status).toUpperCase();
  const className =
    normalized === 'ACTIVE'
      ? 'eats-admin-status eats-admin-status--active'
      : normalized === 'INACTIVE'
        ? 'eats-admin-status eats-admin-status--inactive'
        : 'eats-admin-status eats-admin-status--default';
  return <span className={className}>{status}</span>;
};

const AppUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  useAutoDismissMessage(error, setError);
  useAutoDismissMessage(success, setSuccess);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const canAdd = isEatsAppRootAdmin();

  const closeAddForm = () => {
    setShowAddForm(false);
    setForm(EMPTY_FORM);
    setError('');
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const res = await axiosCustomerInstance.get(CUSTOMER_API_PATHS.appUsers);
      const payload = res?.data;
      const rawRows = Array.isArray(payload)
        ? payload
        : payload?.users || payload?.data || payload?.items || [];
      const normalized = (Array.isArray(rawRows) ? rawRows : [])
        .map(normalizeUserRow)
        .filter(Boolean);
      setUsers(normalized);
    } catch (e) {
      const backendMsg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Unable to load employees.';
      setError(backendMsg);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const roleOptions = useMemo(() => {
    const set = new Set();
    users.forEach((u) => {
      if (u.role) set.add(String(u.role));
    });
    return ['ALL', ...Array.from(set).sort()];
  }, [users]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return users.filter((u) => {
      const roleMatch = roleFilter === 'ALL' || String(u.role) === roleFilter;
      if (!roleMatch) return false;
      if (!query) return true;
      return (
        String(u.id).toLowerCase().includes(query) ||
        String(u.name).toLowerCase().includes(query) ||
        String(u.firstName).toLowerCase().includes(query) ||
        String(u.lastName).toLowerCase().includes(query) ||
        String(u.email).toLowerCase().includes(query) ||
        String(u.aadharNo).toLowerCase().includes(query) ||
        String(u.mobile).toLowerCase().includes(query) ||
        String(u.source).toLowerCase().includes(query)
      );
    });
  }, [users, search, roleFilter]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    if (!firstName) {
      setError('First name is required.');
      return;
    }
    if (!lastName) {
      setError('Last name is required.');
      return;
    }

    const aadharErr = validateAadharNumber(form.aadharNumber);
    if (aadharErr) {
      setError(aadharErr);
      return;
    }

    setSaving(true);
    try {
      await axiosRestaurantInstance.post(ORGANIZATION_API_PATHS.eatsBusinessUsers, {
        firstName,
        lastName,
        name: `${firstName} ${lastName}`.trim(),
        email: form.email.trim(),
        contactNumber: form.contactNumber.trim(),
        aadharNumber: sanitizeAadharInput(form.aadharNumber),
        password: form.password,
        roleName: form.roleName,
      });
      setSuccess('Employee created successfully.');
      setForm(EMPTY_FORM);
      setShowAddForm(false);
      await fetchUsers();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Unable to create employee.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (showAddForm && canAdd) {
    return (
      <EatsAdminLayout activeTab="users">
        <div className="eats-admin-create-page">
          <EatsAdminPageHeader
            title="Add Employee"
            subtitle="Create an application-level employee for Maya Eats Business."
            onBack={closeAddForm}
            beforeTitle={
              <nav className="eats-admin-breadcrumb" aria-label="Breadcrumb">
                <button type="button" className="eats-admin-breadcrumb__link" onClick={closeAddForm}>
                  Employees
                </button>
                <span className="eats-admin-breadcrumb__sep" aria-hidden>
                  /
                </span>
                <span className="eats-admin-breadcrumb__current">Add Employee</span>
              </nav>
            }
          />

          <div className="eats-admin-panel">
            {error ? <div className="eats-admin-error">{error}</div> : null}
            <form onSubmit={handleCreate}>
              <div className="eats-admin-form-grid employees-form-grid">
                <label className="employees-field">
                  <span>First name</span>
                  <input
                    className="eats-admin-input"
                    required
                    value={form.firstName}
                    onChange={(e) => updateForm('firstName', e.target.value)}
                    placeholder="First name"
                  />
                </label>
                <label className="employees-field">
                  <span>Last name</span>
                  <input
                    className="eats-admin-input"
                    required
                    value={form.lastName}
                    onChange={(e) => updateForm('lastName', e.target.value)}
                    placeholder="Last name"
                  />
                </label>
                <label className="employees-field">
                  <span>Email</span>
                  <input
                    className="eats-admin-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => updateForm('email', e.target.value)}
                    placeholder="name@company.com"
                  />
                </label>
                <label className="employees-field">
                  <span>Mobile</span>
                  <input
                    className="eats-admin-input"
                    required
                    value={form.contactNumber}
                    onChange={(e) => updateForm('contactNumber', e.target.value)}
                    placeholder="Mobile number"
                  />
                </label>
                <label className="employees-field">
                  <span>Aadhaar</span>
                  <input
                    className="eats-admin-input"
                    required
                    value={formatAadharDisplay(form.aadharNumber)}
                    onChange={(e) => updateForm('aadharNumber', sanitizeAadharInput(e.target.value))}
                    maxLength={14}
                    inputMode="numeric"
                    placeholder="XXXX XXXX XXXX"
                  />
                </label>
                <label className="employees-field">
                  <span>Password</span>
                  <input
                    className="eats-admin-input"
                    required
                    type="password"
                    value={form.password}
                    onChange={(e) => updateForm('password', e.target.value)}
                    placeholder="Temporary password"
                  />
                </label>
                <label className="employees-field">
                  <span>Role</span>
                  <select
                    className="eats-admin-input"
                    value={form.roleName}
                    onChange={(e) => updateForm('roleName', e.target.value)}
                  >
                    <option value="APP_USER">APP_USER</option>
                    <option value="APP_ADMIN">APP_ADMIN</option>
                  </select>
                </label>
              </div>
              <div className="eats-admin-actions">
                <button
                  type="button"
                  className="eats-admin-btn eats-admin-btn--secondary"
                  onClick={closeAddForm}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="eats-admin-btn eats-admin-btn--primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </EatsAdminLayout>
    );
  }

  return (
    <EatsAdminLayout activeTab="users">
      <h2 className="eats-admin-dashboard__title">Employees</h2>
      <p className="eats-admin-dashboard__subtitle">
        Application-level admins and employees across Maya Eats Business.
      </p>

      <div className="eats-admin-panel">
        <div className="eats-admin-search-bar eats-admin-search-bar--employees">
          <input
            className="eats-admin-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by id, name, email, aadhar, mobile..."
          />
          <select
            className="eats-admin-input"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            {roleOptions.map((role) => (
              <option key={role} value={role}>
                {role === 'ALL' ? 'All roles' : role}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="eats-admin-btn eats-admin-btn--secondary"
            onClick={fetchUsers}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          {canAdd ? (
            <button
              type="button"
              className="eats-admin-btn eats-admin-btn--create"
              onClick={() => {
                setError('');
                setShowAddForm(true);
              }}
            >
              Add Employee
            </button>
          ) : null}
        </div>

        {error ? <div className="eats-admin-error">{error}</div> : null}
        {success ? <div className="eats-admin-success">{success}</div> : null}

        <div className="eats-admin-table-wrap">
          <table className="eats-admin-table">
            <thead>
              <tr>
                <th>User ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Aadhar No</th>
                <th>Mobile</th>
                <th>Role</th>
                <th>Source</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="eats-admin-table__empty">
                    {loading ? 'Loading employees...' : 'No employees found.'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => (
                  <tr key={u.id}>
                    <td>{u.id}</td>
                    <td>{u.name || `${u.firstName} ${u.lastName}`.trim() || '-'}</td>
                    <td>{u.email || '-'}</td>
                    <td>{formatAadharDisplayOrFallback(u.aadharNo, '-')}</td>
                    <td className="employees-mobile">{formatMobileDisplayOrFallback(u.mobile, '-')}</td>
                    <td>{u.role || '-'}</td>
                    <td>{u.source || '-'}</td>
                    <td>{renderStatusBadge(u.status)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </EatsAdminLayout>
  );
};

export default AppUsersPage;
